// TOVIS · Módulo TUBITOS · tela principal (cartões por agente, avisos, histórico, WhatsApp).
// Desenho aprovado pelo Walber no Claude Design em 28/10/2026 (artefato "TUBITOS (proposta)").
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BiotechIcon from '@mui/icons-material/Biotech';
import CheckIcon from '@mui/icons-material/Check';
import { tubitosApi, TUBITOS_COR, C, rotuloCurto, rotuloSituacao, copiarTexto } from './tubitosApi';
import type { Painel, ResumoAgente, Historico, AvisoTubito } from './tubitosApi';

type Props = { onVoltar?: () => void; onAbrirBoletim?: (fad01Id: number) => void }; // [AJUSTAR] mesmas props que o App.tsx passa às telas do LIRAa/Laboratório

const CHAVE_AGENTE = 'tovis-tubitos-agente';
const NUM = { fontVariantNumeric: 'tabular-nums', fontWeight: 800, letterSpacing: '0.01em' } as const;

function lerAgenteGuardado(): number | null {
  try { const v = sessionStorage.getItem(CHAVE_AGENTE); return v ? Number(v) : null; } catch { return null; }
}
function guardarAgente(id: number | null) {
  try { if (id === null) sessionStorage.removeItem(CHAVE_AGENTE); else sessionStorage.setItem(CHAVE_AGENTE, String(id)); } catch { /* ignora */ }
}

function Selo({ fundo, cor, children }: { fundo: string; cor: string; children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ flexShrink: 0, px: 1.25, py: 0.5, borderRadius: 999, fontSize: 13, fontWeight: 700, bgcolor: fundo, color: cor, whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

function Cabecalho({ onVoltar, direita }: { onVoltar?: () => void; direita?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {onVoltar && (
          <IconButton aria-label="Voltar ao menu" onClick={onVoltar} sx={{ width: 44, height: 44 }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: TUBITOS_COR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <BiotechIcon sx={{ color: '#fff', fontSize: 30 }} />
        </Box>
        <Box>
          <Box component="h1" sx={{ m: 0, fontSize: 30, lineHeight: 1.1, letterSpacing: '0.02em', color: C.texto }}>TUBITOS</Box>
          <Box component="p" sx={{ m: 0, mt: 0.5, fontSize: 16, color: C.fraco }}>Próximo número da etiqueta de cada agente</Box>
        </Box>
      </Box>
      {direita}
    </Box>
  );
}

function CartaoAgente({ a, onHistorico }: { a: ResumoAgente; onHistorico: () => void }) {
  const conferir = a.situacao === 'conferir';
  const sem = a.proximo === null;
  const borda = conferir ? `2px solid ${C.ambar}` : sem ? `1px dashed ${C.cinzaBorda}` : `1px solid ${C.borda}`;
  const aviso: AvisoTubito | undefined = a.avisos[0];
  return (
    <Box component="article" sx={{ bgcolor: '#fff', border: borda, borderRadius: '16px', p: '18px 20px', display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 236, boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box component="h2" sx={{ m: 0, fontSize: 21, lineHeight: 1.2, color: C.texto }}>{a.nome}</Box>
        {conferir && <Selo fundo={C.seloAmbar} cor={C.seloAmbarTexto}>Conferir</Selo>}
        {!conferir && sem && <Selo fundo={C.cinzaSelo} cor={C.cinzaTexto}>Sem registro</Selo>}
        {!conferir && !sem && a.perto_999 && <Selo fundo={C.seloAzul} cor={C.azulTexto}>Perto do 999</Selo>}
      </Box>

      {!sem && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box component="span" sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', color: C.fraco }}>PRÓXIMO NÚMERO</Box>
            <Box component="span" sx={{ ...NUM, fontSize: 64, lineHeight: 1, color: TUBITOS_COR }}>{a.proximo}</Box>
          </Box>
          <Box sx={{ fontSize: 15, lineHeight: 1.45, color: C.suave }}>
            Último entregue: <b style={{ color: C.texto }}>{a.ultimo}</b> · {a.data_ultimo_texto}
            <br />
            <span style={{ color: C.fraco }}>Boletim com {a.tubitos_ultimo_texto}</span>
          </Box>
        </>
      )}

      {sem && (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75, p: 1.75, border: `1px dashed ${C.cinzaBorda}`, borderRadius: '12px', bgcolor: '#F7F8FA' }}>
          <Box component="span" sx={{ fontSize: 17, fontWeight: 700, color: C.suave }}>Ainda sem número</Box>
          <Box component="span" sx={{ fontSize: 15, color: C.fraco, lineHeight: 1.4 }}>Aparece aqui assim que você lançar o 1º tubito no FAD-01.</Box>
        </Box>
      )}

      {aviso && (
        <Box sx={{ bgcolor: C.ambarFundo, borderRadius: '10px', p: '10px 12px', fontSize: 15, lineHeight: 1.4, color: C.ambarTexto }}>
          {aviso.texto}{a.avisos.length > 1 ? ` (+${a.avisos.length - 1} aviso${a.avisos.length > 2 ? 's' : ''})` : ''}
        </Box>
      )}
      {!aviso && !sem && a.perto_999 && (
        <Box sx={{ bgcolor: C.azulFundo, borderRadius: '10px', p: '10px 12px', fontSize: 15, lineHeight: 1.4, color: C.azulTexto }}>
          Depois do 999, a próxima etiqueta volta para o 1.
        </Box>
      )}

      {!sem && (
        <Button onClick={onHistorico} sx={{ mt: 'auto', alignSelf: 'flex-start', minHeight: 44, px: 0, fontSize: 15, fontWeight: 700, textTransform: 'none', color: TUBITOS_COR }}>
          Ver histórico
        </Button>
      )}
    </Box>
  );
}

function JanelaWhatsApp({ aberta, onFechar }: { aberta: boolean; onFechar: () => void }) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState(false);
  useEffect(() => {
    if (!aberta) return;
    setCopiado(false); setErro(''); setTexto('');
    tubitosApi.mensagem().then((r) => setTexto(r.texto)).catch((e: any) => setErro(e?.message || 'Não foi possível montar a mensagem.'));
  }, [aberta]);
  const copiar = async () => { setCopiado(await copiarTexto(texto)); };
  return (
    <Dialog open={aberta} onClose={onFechar} fullWidth maxWidth="sm" aria-labelledby="tubitos-whats-titulo">
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Box component="h2" id="tubitos-whats-titulo" sx={{ m: 0, fontSize: 22, color: C.texto }}>Mensagem para o grupo</Box>
          <IconButton aria-label="Fechar" onClick={onFechar} sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>
        </Box>
        <Box component="p" sx={{ m: 0, fontSize: 15, color: C.fraco }}>Pronta para colar. As palavras entre asteriscos saem em negrito no WhatsApp.</Box>
        {erro && <Box sx={{ color: C.vermelho, fontSize: 15 }}>{erro}</Box>}
        {!erro && !texto && <CircularProgress size={24} />}
        {texto && (
          <Box component="pre" sx={{ m: 0, bgcolor: C.verdeChip, border: '1px solid #CFE5DF', borderRadius: '14px', p: '18px 20px', fontFamily: 'ui-monospace, "Roboto Mono", monospace', fontSize: 16, lineHeight: 1.6, color: C.texto, whiteSpace: 'pre-wrap' }}>
            {texto}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
          <Button variant="contained" disableElevation onClick={copiar} disabled={!texto} startIcon={<ContentCopyIcon />}
            sx={{ flexGrow: 1, minHeight: 52, borderRadius: '12px', bgcolor: TUBITOS_COR, fontSize: 18, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#0B5A54' } }}>
            Copiar mensagem
          </Button>
          <Button variant="outlined" onClick={onFechar} sx={{ minHeight: 52, borderRadius: '12px', fontSize: 17, fontWeight: 700, textTransform: 'none', color: C.texto, borderColor: C.cinzaBorda }}>
            Fechar
          </Button>
        </Box>
        {copiado && (
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: C.verdeFundo, borderRadius: '10px', p: '10px 14px', fontSize: 15, color: C.verdeTexto }}>
            <CheckIcon fontSize="small" /> <span><b>Copiado!</b> Agora é só colar no grupo.</span>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

function HistoricoAgente({ agenteId, onVoltar, onAbrirBoletim }: { agenteId: number; onVoltar: () => void; onAbrirBoletim?: (id: number) => void }) {
  const [dados, setDados] = useState<Historico | null>(null);
  const [erro, setErro] = useState('');
  const [limite, setLimite] = useState(20);
  useEffect(() => {
    setErro('');
    tubitosApi.historico(agenteId, limite).then(setDados).catch((e: any) => setErro(e?.message || 'Não foi possível carregar o histórico.'));
  }, [agenteId, limite]);
  const a = dados?.agente;
  const cel = { p: '14px 18px', borderTop: `1px solid #E6E9ED`, verticalAlign: 'middle' } as const;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Button onClick={onVoltar} startIcon={<ArrowBackIcon />} sx={{ alignSelf: 'flex-start', minHeight: 44, fontSize: 16, fontWeight: 700, textTransform: 'none', color: TUBITOS_COR }}>
        Voltar para TUBITOS
      </Button>
      {erro && <Box sx={{ color: C.vermelho, fontSize: 15 }}>{erro}</Box>}
      {!dados && !erro && <CircularProgress size={28} />}
      {a && (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2.5, bgcolor: '#fff', border: `1px solid ${C.borda}`, borderRadius: '16px', p: '22px 24px' }}>
            <Box>
              <Box component="h2" sx={{ m: 0, fontSize: 30, color: C.texto }}>{a.nome}</Box>
              <Box component="p" sx={{ m: 0, mt: 0.75, fontSize: 16, color: C.fraco }}>
                {a.ultimo !== null ? <>Último entregue: <b style={{ color: C.texto }}>{a.ultimo}</b> · {a.data_ultimo_texto}</> : 'Ainda sem número registrado.'}
              </Box>
            </Box>
            {a.proximo !== null && (
              <Box sx={{ textAlign: 'right' }}>
                <Box sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', color: C.fraco }}>PRÓXIMO NÚMERO</Box>
                <Box sx={{ ...NUM, fontSize: 72, lineHeight: 1, color: TUBITOS_COR }}>{a.proximo}</Box>
              </Box>
            )}
          </Box>
          {dados && dados.itens.length > 0 && (
            <Box sx={{ bgcolor: '#fff', border: `1px solid ${C.borda}`, borderRadius: '16px', overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, minWidth: 560 }}>
                <thead>
                  <Box component="tr" sx={{ bgcolor: '#EEF1F4', textAlign: 'left', color: C.cinzaTexto, fontSize: 14 }}>
                    <Box component="th" sx={{ p: '12px 18px' }}>Data</Box>
                    <Box component="th" sx={{ p: '12px 18px' }}>Etiquetas</Box>
                    <Box component="th" sx={{ p: '12px 18px' }}>Situação</Box>
                    <Box component="th" sx={{ p: '12px 18px' }}>Boletim</Box>
                  </Box>
                </thead>
                <tbody>
                  {dados.itens.map((it) => {
                    const s = rotuloSituacao(it.situacao, it.avisos);
                    return (
                      <React.Fragment key={it.fad01_id}>
                        <tr>
                          <Box component="td" sx={cel}>{it.data_texto}{it.eh_pe ? <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 700, color: C.cinzaTexto }}>PE</Box> : null}</Box>
                          <Box component="td" sx={{ ...cel, ...NUM, fontSize: 19 }}>{it.faixa_texto || '—'}</Box>
                          <Box component="td" sx={cel}><Selo fundo={s.fundo} cor={s.cor}>{s.texto}</Selo></Box>
                          <Box component="td" sx={cel}>
                            {onAbrirBoletim ? (
                              <Button onClick={() => onAbrirBoletim(it.fad01_id)} sx={{ minHeight: 44, px: 0, fontWeight: 700, textTransform: 'none', color: TUBITOS_COR }}>Abrir no FAD-01</Button>
                            ) : `#${it.fad01_id}`}
                          </Box>
                        </tr>
                        {it.avisos.length > 0 && (
                          <tr>
                            <Box component="td" colSpan={4} sx={{ p: '0 18px 14px' }}>
                              {it.avisos.map((av, k) => (
                                <Box key={k} sx={{ bgcolor: C.ambarFundo, border: `1px dashed ${C.ambar}`, borderRadius: '10px', p: '12px 14px', fontSize: 15, lineHeight: 1.45, color: C.ambarTexto, mt: k ? 1 : 0 }}>
                                  <b>{av.texto}</b>
                                  {av.tipo === 'pulo' ? ` Pergunte a ${a.nome}: etiqueta perdida, tubito descartado ou erro de anotação? Se for erro, corrija o nº no boletim pelo FAD-01.` : ''}
                                  {av.tipo === 'repetido' && av.fad01_id_outro && onAbrirBoletim ? (
                                    <Button onClick={() => onAbrirBoletim(av.fad01_id_outro as number)} sx={{ ml: 1, minHeight: 32, py: 0, textTransform: 'none', fontWeight: 700, color: C.ambarTexto }}>Abrir o outro boletim</Button>
                                  ) : null}
                                </Box>
                              ))}
                            </Box>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Box>
            </Box>
          )}
          {dados && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, fontSize: 15, color: C.fraco }}>
              <span>Mais recente no topo. Mostrando {dados.itens.length} de {dados.total} boletim(ns) com tubito.</span>
              {dados.total > dados.itens.length && (
                <Button variant="outlined" onClick={() => setLimite(500)} sx={{ minHeight: 44, borderRadius: '10px', textTransform: 'none', fontWeight: 700, color: C.texto, borderColor: C.cinzaBorda }}>Ver todos</Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default function Tubitos({ onVoltar, onAbrirBoletim }: Props) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [agenteSel, setAgenteSel] = useState<number | null>(lerAgenteGuardado());
  const [whats, setWhats] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try { setPainel(await tubitosApi.painel()); }
    catch (e: any) { setErro(e?.message || 'Não foi possível carregar o TUBITOS.'); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirAgente = (id: number | null) => { guardarAgente(id); setAgenteSel(id); if (id === null) carregar(); };

  const agentesComAviso = painel ? painel.agentes.filter((a) => a.avisos.length > 0) : [];

  return (
    <Box sx={{ bgcolor: C.fundo, minHeight: '100vh', boxSizing: 'border-box', p: { xs: '20px 16px 48px', md: '28px 24px 48px' }, color: C.texto }}>
      <Box sx={{ maxWidth: 1220, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {agenteSel !== null ? (
          <>
            <Cabecalho onVoltar={onVoltar} />
            <HistoricoAgente agenteId={agenteSel} onVoltar={() => abrirAgente(null)} onAbrirBoletim={onAbrirBoletim} />
          </>
        ) : (
          <>
            <Cabecalho
              onVoltar={onVoltar}
              direita={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  <IconButton aria-label="Atualizar" onClick={carregar} sx={{ width: 48, height: 48, border: `1px solid ${C.borda}`, bgcolor: '#fff' }}><RefreshIcon /></IconButton>
                  <Button variant="contained" disableElevation startIcon={<ContentCopyIcon />} onClick={() => setWhats(true)}
                    sx={{ minHeight: 48, px: 2.5, borderRadius: '12px', bgcolor: TUBITOS_COR, fontSize: 17, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#0B5A54' } }}>
                    Copiar para o WhatsApp
                  </Button>
                </Box>
              }
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px 28px', alignItems: 'center', bgcolor: '#fff', border: `1px solid ${C.borda}`, borderRadius: '14px', p: '14px 18px', fontSize: 15, color: C.suave }}>
              <span><b style={{ color: C.texto }}>Atualizado</b> {painel?.atualizado_ate_texto ? `com os boletins até ${painel.atualizado_ate_texto}` : '— ainda sem nenhum nº lançado'}</span>
              <span><b style={{ color: C.texto }}>Regra:</b> cada agente numera de 1 a 999, sem pular. Depois do 999, volta ao 1.</span>
              <span><b style={{ color: C.texto }}>LIRAa</b> tem numeração própria e não entra aqui.</span>
            </Box>

            {erro && <Box sx={{ bgcolor: '#FDECEC', color: C.vermelho, borderRadius: '12px', p: 2, fontSize: 15 }}>{erro}</Box>}
            {carregando && !painel && <CircularProgress size={32} />}

            {agentesComAviso.length > 0 && (
              <Box component="section" aria-label="Avisos" sx={{ bgcolor: '#FFF7E6', border: '1px solid #F2C46D', borderRadius: '14px', p: '16px 18px', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 18, fontWeight: 700, color: '#7A4A00' }}>
                  <WarningAmberIcon />
                  {agentesComAviso.length === 1 ? '1 agente precisa de conferência' : `${agentesComAviso.length} agentes precisam de conferência`}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  {agentesComAviso.flatMap((a) => a.avisos.map((av, k) => (
                    <Button key={`${a.agente_id}-${k}`} onClick={() => abrirAgente(a.agente_id)}
                      sx={{ minHeight: 44, px: 2, bgcolor: '#fff', border: '1px solid #E8B85A', borderRadius: '10px', color: C.ambarTexto, fontSize: 16, textTransform: 'none', '&:hover': { bgcolor: '#FFFBF2' } }}>
                      <b>{a.nome}</b>&nbsp;· {rotuloCurto(av)}
                    </Button>
                  )))}
                </Box>
              </Box>
            )}

            {painel && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 2 }}>
                {painel.agentes.map((a) => (
                  <CartaoAgente key={a.agente_id} a={a} onHistorico={() => abrirAgente(a.agente_id)} />
                ))}
              </Box>
            )}
            <JanelaWhatsApp aberta={whats} onFechar={() => setWhats(false)} />
          </>
        )}
      </Box>
    </Box>
  );
}
